import React, { ChangeEvent } from 'react';
import { observer } from 'mobx-react-lite';
import { PrimaryButton } from '@JohnLewisPartnership/jl-design-system/dist/elements/button/Button';
import { Message } from '@JohnLewisPartnership/jl-design-system/dist/components/message/Message';
import { DEPRECATED_Select as Select } from '@JohnLewisPartnership/jl-design-system/dist/elements/select/Select';
import { LoadingSpinner } from '@JohnLewisPartnership/jl-design-system/dist/components/loading-spinner/LoadingSpinner';
import styles from './WishListVariants.module.scss';
import WishListsStore from '../../../stores/WishListsStore';
import ColourList from '@JohnLewisPartnership/jl-design-system/dist/components/colour/ColourList';
import Colour from '@JohnLewisPartnership/jl-design-system/dist/components/colour/Colour';

interface WishListVariantsProps {
    store: WishListsStore;
    onClose(): void;
}

export const WishListVariants: React.FC<WishListVariantsProps> = observer(({ store, onClose }) => {
    if (!store.currentProduct) return null;

    const { colours = [], url } = store.currentProduct;
    const buttonText = url ? 'Go to product page' : undefined;
    const buttonProps = url
        ? {
              href: url,
              rel: 'noopener noreferrer',
              tag: 'a',
          }
        : undefined;

    async function handleColourClick(colour: string): Promise<void> {
        if (store.selectedColour != colour) {
            store.variantChanged = true;
        }

        // Hold onto the current PDP selection in case of a cancel
        if (!store.pdpSelectedSku) {
            store.pdpSelectedSku = store.currentProductSkuId;
        }

        store.setSelectedColour(colour);
        const variant = store.currentProduct?.skus?.find((p) => p.colour === colour);
        if (variant) {
            store.setCurrentProductSkuId(variant.id);
        }
    }

    async function handleSizeChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
        if (store.selectedSize != event.target.value) {
            store.variantChanged = true;
        }

        // Hold onto the current PDP selection in case of a cancel
        if (!store.pdpSelectedSku) {
            store.pdpSelectedSku = store.currentProductSkuId;
        }

        store.setSelectedSize(event.target.value);
        const variant = store.currentProduct?.skus?.find(
            (p) => p.colour === store.selectedColour && p.size === event.target.value,
        );
        if (variant) {
            store.setCurrentProductSkuId(variant.id);
        }
    }

    async function onConfirm(): Promise<void> {
        if (store.hasMoreThanOneWishlist) {
            store.dispatchGlobalEvent(
                'global.interaction',
                store.getActionType() + ' - wishlist',
                'confirm chosen variant',
                store.currentProduct,
                undefined,
            );
            store.setModal('wishlist');
        } else {
            store.dispatchGlobalEvent(
                'add.wishlist',
                store.getActionType(),
                'add to wishlist',
                store.currentProduct,
                'choose variant',
            );

            await store.addToWishList(store.currentProductSkuId, '');

            if (!store.hasError) {
                if (store.mode === 'icon-placeholder-pdp' && store.variantChanged) {
                    store.updateURL();
                }
                onClose();
            }
        }
    }

    if (store.isCallingAPI && !colours.length && !store.sizesForColour.length) {
        return (
            <div className={styles.loader}>
                <LoadingSpinner size={'large'} />
            </div>
        );
    }

    return (
        <>
            {!!colours.length && (
                <section className={styles.optionsContainer} data-test={'colour-selector'} data-label={'colour-list'}>
                    <ColourList label={`Colour: ${store.selectedColour}`} onClick={handleColourClick}>
                        {colours.map((colour, index) => (
                            <Colour
                                key={index}
                                swatch={colour.swatchImage}
                                name={colour.colour}
                                value={colour.colour}
                                selected={store.selectedColour === colour.colour}
                                unavailable={!colour.inStock}
                                id={`colour-${colour.colour}`}
                            />
                        ))}
                    </ColourList>
                </section>
            )}
            {!!store.sizesForColour.length && (
                <>
                    <section className={styles.optionsContainer} data-test={'size-selector'}>
                        <label htmlFor="size-selector" className={styles.controlLabel}>
                            Size
                        </label>

                        <div className={styles.sizeSelector}>
                            <Select
                                id={'size-selector'}
                                name={'size-selector'}
                                onChange={handleSizeChange}
                                value={store.selectedSize}
                                options={[{ label: 'Select size', value: '' }].concat(
                                    store.sizesForColour
                                        .sort((a, b) => {
                                            function extractNumericValue(str: string): number | null {
                                                if (!Number.isNaN(Number(str))) return parseInt(str, 10);

                                                // Otherwise, extract the first numeric part before any non-numeric characters
                                                const match = str.match(/^(\d+)/);
                                                return match ? parseInt(match[0], 10) : null;
                                            }

                                            const numA = extractNumericValue(a);
                                            const numB = extractNumericValue(b);

                                            if (numA !== null && numB !== null) {
                                                return numA - numB;
                                            } else if (numA !== null) {
                                                return -1;
                                            } else if (numB !== null) {
                                                return 1;
                                            } else {
                                                return 0; // Don't sort string values
                                            }
                                        })
                                        .map((size) => ({
                                            value: size,
                                            label: size,
                                        })),
                                )}
                            />
                        </div>
                    </section>
                </>
            )}

            {!store.hasError ? (
                <div className={styles.footer}>
                    <PrimaryButton
                        className={styles.wishlListConfirmButton}
                        disabled={store.sizesForColour.length && !store.selectedSize}
                        submitting={store.isCallingAPI}
                        onClick={onConfirm}
                    >
                        {store.sizesForColour.length && !store.selectedSize ? `Choose a size first` : 'Confirm'}
                    </PrimaryButton>
                </div>
            ) : (
                <Message
                    type="error"
                    body="Please try again or go to the product page to add it to your wish list."
                    buttonText={buttonText}
                    buttonProps={buttonProps}
                    title="Sorry, the item options are not available at the moment"
                />
            )}
        </>
    );
});
